





























































































































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import moment from 'moment';
import { Station } from '@/models/station';
import { Customer } from '@/models/customer';
import { filterReportStations, isExcludedReportStation } from '@/misc/reportCustomerFilter';

const StationStore = namespace('station');

interface MaintenanceMachineRow {
  id: string;
  orderNo: string;
  serialNo: string;
  lastMaintenance: string;
  nextMaintenance: string;
  nextMaintenanceDate?: Date;
  customerName: string;
  stationName: string;
}

interface CustomerStatsRow {
  customerName: string;
  stationCount: number;
  openMaintenanceCount: number;
  overdueMaintenanceCount: number;
}

type OpsFilterKey = 'maintenance' | 'inventory' | 'overdue';

@Component({
  components: {
    ReportsNav: () => import('@/components/ReportsNav.component.vue'),
    ReportsPageHeader: () => import('@/components/ReportsPageHeader.component.vue'),
    DashboardStatCard: () => import('@/components/DashboardStatCard.component.vue'),
    MaintenanceOverviewMap: () =>
      import('@/components/charts/MaintenanceOverviewMap.component.vue'),
    MaintenanceByCustomerTable: () =>
      import('@/components/charts/MaintenanceByCustomerTable.component.vue'),
  },
})
export default class ReportsOverviewView extends Vue {
  @StationStore.Action('loadAllStations')
  private loadAllStations!: () => Promise<Station[]>;

  @StationStore.Getter('allStations')
  private allStations!: Station[];

  private get reportStations(): Station[] {
    return filterReportStations(this.allStations);
  }

  public stationsLoading = true;
  public activeTab = 0;
  public activeOpsFilter: OpsFilterKey = 'maintenance';

  public maintenanceCount = 0;
  public maintenanceMachines: MaintenanceMachineRow[] = [];
  public inventoryStations: Station[] = [];

  public maintenanceSearch = '';
  public overdueSearch = '';
  public inventorySearch = '';
  public itemsPerPage = 10;

  public maintenanceHeaders = [
    { text: 'Kunde', value: 'customerName' },
    { text: 'Station', value: 'stationName' },
    { text: 'Auftragsnummer', value: 'orderNo' },
    { text: 'Seriennummer', value: 'serialNo' },
    { text: 'Letzte Wartung', value: 'lastMaintenance' },
    { text: 'Nächste Wartung ab', value: 'nextMaintenance' },
    { text: '', value: 'details', sortable: false, align: 'end', width: 60 },
  ];

  public inventoryHeaders = [
    { text: 'Kunde', value: 'customerName' },
    { text: 'Station', value: 'name' },
    { text: 'Anschrift', value: 'address' },
    { text: 'PLZ', value: 'zipCode' },
    { text: 'Stadt', value: 'city' },
    { text: 'Maschinen', value: 'machineCount' },
    { text: '', value: 'details', sortable: false, align: 'end', width: 60 },
  ];

  public get overdueMaintenanceCount(): number {
    return this.maintenanceMachines.filter((machine) => this.isOverdue(machine)).length;
  }

  public get overdueMaintenanceMachines(): MaintenanceMachineRow[] {
    return this.maintenanceMachines.filter((machine) => this.isOverdue(machine));
  }

  public get customerTableRows(): CustomerStatsRow[] {
    const stats = new Map<string, CustomerStatsRow>();

    this.maintenanceMachines.forEach((machine) => {
      const existing = stats.get(machine.customerName) || {
        customerName: machine.customerName,
        stationCount: 0,
        openMaintenanceCount: 0,
        overdueMaintenanceCount: 0,
      };
      existing.openMaintenanceCount += 1;
      if (this.isOverdue(machine)) {
        existing.overdueMaintenanceCount += 1;
      }
      stats.set(machine.customerName, existing);
    });

    this.allStations.forEach((station) => {
      if (isExcludedReportStation(station)) return;

      const customerName = (station.customer as Customer).name;
      const hasOpenMaintenance = station.machines.some((machine) =>
        this.maintenanceMachines.some((row) => row.id === machine.id),
      );
      if (!hasOpenMaintenance) return;

      const existing = stats.get(customerName) || {
        customerName,
        stationCount: 0,
        openMaintenanceCount: 0,
        overdueMaintenanceCount: 0,
      };
      existing.stationCount += 1;
      stats.set(customerName, existing);
    });

    let rows = [...stats.values()];

    switch (this.activeOpsFilter) {
      case 'overdue':
        rows = rows
          .filter((row) => row.overdueMaintenanceCount > 0)
          .sort((a, b) => b.overdueMaintenanceCount - a.overdueMaintenanceCount);
        break;
      case 'inventory': {
        const inventoryCustomers = new Set(
          this.inventoryStations.map((station) => (station.customer as Customer).name),
        );
        rows = rows
          .filter((row) => inventoryCustomers.has(row.customerName))
          .map((row) => ({
            ...row,
            stationCount: this.inventoryStations.filter(
              (station) => (station.customer as Customer).name === row.customerName,
            ).length,
          }))
          .sort((a, b) => b.stationCount - a.stationCount);
        break;
      }
      default:
        rows.sort((a, b) => b.openMaintenanceCount - a.openMaintenanceCount);
    }

    return rows;
  }

  public get filteredMapStations(): Station[] {
    switch (this.activeOpsFilter) {
      case 'overdue':
        return this.reportStations.filter((station) =>
          this.overdueMaintenanceMachines.some((machine) =>
            station.machines.some((m) => m.id === machine.id),
          ),
        );
      case 'inventory':
        return this.inventoryStations;
      case 'maintenance':
      default:
        return this.reportStations.filter((station) =>
          this.maintenanceMachines.some((machine) =>
            station.machines.some((m) => m.id === machine.id),
          ),
        );
    }
  }

  public get salesPanelTitle(): string {
    switch (this.activeOpsFilter) {
      case 'overdue':
        return 'Überfällige Wartungen je Kunde';
      case 'inventory':
        return 'Bestandsaufnahmen je Kunde';
      default:
        return 'Offene Wartungen je Kunde';
    }
  }

  public get salesPanelSubtitle(): string {
    switch (this.activeOpsFilter) {
      case 'overdue':
        return 'Kunden mit überfälligen Wartungen';
      case 'inventory':
        return 'Kunden mit aktiver Bestandsaufnahme';
      default:
        return 'Kunden mit fälligen Wartungen';
    }
  }

  public setOpsFilter(filter: OpsFilterKey, tab: number | null) {
    this.activeOpsFilter = filter;
    if (tab !== null) {
      this.activeTab = tab;
    }
  }

  @Watch('activeTab')
  private onActiveTabChange(tab: number) {
    const filterByTab: OpsFilterKey[] = ['maintenance', 'overdue', 'inventory'];
    this.activeOpsFilter = filterByTab[tab] || 'maintenance';
  }

  public get affectedCustomerCount(): number {
    return new Set(this.maintenanceMachines.map((machine) => machine.customerName)).size;
  }

  public get overdueSharePercent(): number {
    if (!this.maintenanceCount) return 0;
    return Math.round((this.overdueMaintenanceCount / this.maintenanceCount) * 100);
  }

  public get statCards() {
    return [
      {
        key: 'maintenance',
        filter: 'maintenance' as OpsFilterKey,
        tab: 0,
        icon: 'mdi-wrench',
        color: '#344767',
        label: 'Offene Wartungen',
        value: this.maintenanceCount,
        footerHighlight: '',
        footerText: `${this.overdueSharePercent}% davon überfällig`,
        active: this.activeOpsFilter === 'maintenance',
        clickable: true,
      },
      {
        key: 'overdue',
        filter: 'overdue' as OpsFilterKey,
        tab: 1,
        icon: 'mdi-alert-circle',
        color: '#e91e63',
        label: 'Überfällig',
        value: this.overdueMaintenanceCount,
        footerHighlight: '',
        footerText: 'Wartung überfällig',
        active: this.activeOpsFilter === 'overdue',
        clickable: true,
      },
      {
        key: 'inventory',
        filter: 'inventory' as OpsFilterKey,
        tab: 2,
        icon: 'mdi-clipboard-text',
        color: '#4CAF50',
        label: 'Bestandsaufnahmen',
        value: this.inventoryStations.length,
        footerHighlight: '',
        footerText: 'Aktive Stationen',
        active: this.activeOpsFilter === 'inventory',
        clickable: true,
      },
      {
        key: 'stations',
        filter: null,
        tab: null,
        icon: 'mdi-map-marker',
        color: '#1A73E8',
        label: 'Stationen',
        value: this.reportStations.length,
        footerHighlight: String(this.affectedCustomerCount),
        footerText: 'Kunden mit offenen Wartungen',
        active: false,
        clickable: false,
      },
    ];
  }

  public async mounted() {
    await this.loadStationData();
  }

  private async loadStationData() {
    try {
      await this.loadAllStations();
      this.inventoryStations = this.reportStations.filter((station) => station.inventoryActive);
      this.maintenanceMachines = this.reportStations
        .flatMap((station) => this.getMaintenanceMachines(station))
        .sort((a, b) => a.nextMaintenanceDate!.getTime() - b.nextMaintenanceDate!.getTime());
      this.maintenanceCount = this.maintenanceMachines.length;
    } finally {
      this.stationsLoading = false;
    }
  }

  private getMaintenanceMachines(station: Station): MaintenanceMachineRow[] {
    const currentDate = moment().endOf('day');
    return station.machines
      .filter((machine) => {
        const nextMaintenance = machine.nextMaintenance
          ? moment(machine.nextMaintenance, 'YYYY-MM-DD').toDate()
          : null;
        return nextMaintenance && nextMaintenance <= currentDate.toDate() && machine.maintenanceActive;
      })
      .map((machine) => ({
        ...machine,
        customerName: (station.customer as Customer).name,
        stationName: station.name,
      }));
  }

  public getCustomerName(station: Station): string {
    return (station.customer as Customer).name;
  }

  public isOverdue(machine: MaintenanceMachineRow): boolean {
    if (!machine.nextMaintenance) return false;
    return moment(machine.nextMaintenance, 'YYYY-MM-DD').isBefore(moment(), 'day');
  }

  public openMachineDetails(machine: MaintenanceMachineRow) {
    const station = this.reportStations.find((s) => s.machines.some((m) => m.id === machine.id));
    if (!station) return;
    this.$router.push({
      name: 'station_details_view',
      params: {
        stationId: station.id,
        customerId: (station.customer as Customer).id,
      },
    });
  }

  public openStationDetails(station: Station) {
    this.$router.push({
      name: 'station_details_view',
      params: {
        stationId: station.id,
        customerId: (station.customer as Customer).id,
      },
    });
  }
}
